import { useEffect, useRef, useState } from "react";
import { statsData } from "@/mocks/landingData";

function AnimatedNumber({
  target,
  duration = 2000,
}: {
  target: string;
  duration?: number;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const numericValue = parseInt(target.replace(/\D/g, ""), 10);
  const prefix = target.startsWith("+") ? "+" : "";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const step = numericValue / (duration / 16);
          const timer = setInterval(() => {
            start += step;
            if (start >= numericValue) {
              setCount(numericValue);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [numericValue, duration]);

  return (
    <div
      ref={ref}
      className="text-3xl md:text-4xl lg:text-5xl font-extrabold v2-gradient-text"
    >
      {prefix}
      {count.toLocaleString("ar-SA")}
    </div>
  );
}

export default function StatsBar() {
  return (
    <section className="relative z-20 -mt-8 px-4 md:px-8 pb-8">
      <div className="container-main">
        <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-slate-900/90 to-slate-950/90 backdrop-blur-xl shadow-[0_0_60px_-15px_rgba(45,212,191,0.25)]">
          <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-cyan-500/5" />
          <div className="relative grid grid-cols-2 lg:grid-cols-4 divide-x divide-x-reverse divide-white/5">
            {statsData.map((stat, i) => (
              <div
                key={stat.label}
                className="flex flex-col items-center justify-center py-10 md:py-12 px-4 text-center group hover:bg-white/[0.02] transition-colors"
              >
                <AnimatedNumber target={stat.number} />
                <span className="text-slate-400 text-sm md:text-base mt-2 font-medium group-hover:text-teal-300/80 transition-colors">
                  {stat.label}
                </span>
                <span className="mt-3 w-8 h-0.5 bg-gradient-to-l from-teal-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
